import type { ColumnTarget, PlanColumn, PlanDraft } from "@weights/plan-reading";
import { addColumn } from "./ops.js";
import { isEveryone } from "./text.js";
import { type Change, change } from "./patch.js";

/** §17.8: a saved group is a shortcut that fills a column, never a week's membership. */
export interface SavedGroup {
  id: string;
  name: string;
  members: string[];
  position: number;
}

export interface TeamAthlete {
  id: string;
  name: string;
}

export interface WhoContext {
  groups: SavedGroup[];
  athletes: TeamAthlete[];
}

export const EVERYONE_NAME = "Everyone";
const PLACEHOLDER_NAMES = new Set(["", "new column"]);

export function hasTargets(draft: PlanDraft): boolean {
  return draft.columns.some((c) => c.target !== undefined);
}

/**
 * Who gets which column (§17.8): the first `people` column naming them, else the `everyone`
 * column, else nothing. `byAthlete` maps every athlete given to a column id or null.
 */
export interface Assignments {
  byAthlete: Map<string, string | null>;
  byColumn: Map<string, string[]>;
  nothing: string[];
}

export function assign(columns: PlanColumn[], athletes: TeamAthlete[]): Assignments {
  const byAthlete = new Map<string, string | null>();
  const byColumn = new Map<string, string[]>(columns.map((c) => [c.id, []]));
  const everyone = columns.find((c) => c.target?.kind === "everyone");
  const nothing: string[] = [];
  for (const athlete of athletes) {
    const column = columns.find((c) => c.target?.kind === "people" && c.target.ids.includes(athlete.id)) ?? everyone;
    byAthlete.set(athlete.id, column?.id ?? null);
    if (column) byColumn.get(column.id)!.push(athlete.id);
    else nothing.push(athlete.id);
  }
  return { byAthlete, byColumn, nothing };
}

/** First names, unless two athletes share one; then their full names. */
export function shortNames(athletes: TeamAthlete[]): Map<string, string> {
  const first = (n: string) => n.trim().split(/\s+/)[0] ?? n;
  const counts = new Map<string, number>();
  for (const a of athletes) counts.set(first(a.name), (counts.get(first(a.name)) ?? 0) + 1);
  return new Map(athletes.map((a) => [a.id, (counts.get(first(a.name)) ?? 0) > 1 ? a.name : first(a.name)]));
}

/** "Jo", "Jo and Sam", "Jo, Sam and Lee", "Jo, Sam and 3 others". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length <= 3) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
}

export function nothingWarning(names: string[]): string {
  return names.length === 1 ? `${names[0]} gets nothing this week` : `${joinNames(names)} get nothing this week`;
}

/** Under a heading: "Robin, Sam, Lee", "Robin, Sam +3", "Everyone else". */
export function reachSummary(column: PlanColumn, columns: PlanColumn[], reached: string[], names: Map<string, string>): string {
  const target = column.target;
  if (!target) return "Reaches no one: choose who";
  if (target.kind === "everyone") return columns.some((c) => c.target?.kind === "people") ? "Everyone else" : "Everyone";
  const shown = reached.map((id) => names.get(id)).filter((n): n is string => !!n);
  if (shown.length === 0) return target.ids.length ? "No one: all in earlier columns" : "No one yet";
  if (shown.length <= 3) return shown.join(", ");
  return `${shown.slice(0, 2).join(", ")} +${shown.length - 2}`;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const x = new Set(a);
  const y = new Set(b);
  return x.size === y.size && [...y].every((id) => x.has(id));
}

/** The saved group this column was filled from, when its members have changed since. */
export function staleGroup(column: PlanColumn, groups: SavedGroup[]): SavedGroup | null {
  const target = column.target;
  if (target?.kind !== "people" || !target.groupId) return null;
  const group = groups.find((g) => g.id === target.groupId);
  if (!group || sameSet(group.members, target.ids)) return null;
  return group;
}

/** The name a target gives a column (§17.8): the group's, else the athletes', else Everyone. */
export function autoName(target: ColumnTarget | undefined, ctx: WhoContext): string | null {
  if (!target) return null;
  if (target.kind === "everyone") return EVERYONE_NAME;
  const group = target.groupId ? ctx.groups.find((g) => g.id === target.groupId) : undefined;
  if (group) return group.name;
  const names = shortNames(ctx.athletes);
  const shown = target.ids.map((id) => names.get(id)).filter((n): n is string => !!n);
  return shown.length ? shown.join("/") : null;
}

/** He hasn't named it himself: it is blank, a placeholder, or what its target gave it. */
export function nameFollowsTarget(column: PlanColumn, ctx: WhoContext): boolean {
  if (PLACEHOLDER_NAMES.has(column.name.trim().toLowerCase())) return true;
  return column.name === autoName(column.target, ctx);
}

/** One step: the column's new target, and its name when the name followed the old one. */
export function setTarget(draft: PlanDraft, c: number, target: ColumnTarget | undefined, ctx: WhoContext): Change[] {
  const column = draft.columns[c];
  if (!column) return [];
  const out: Change[] = [change(draft, ["columns", c, "target"], target)];
  if (nameFollowsTarget(column, ctx)) {
    const name = autoName(target, ctx);
    if (name && name !== column.name) out.push(change(draft, ["columns", c, "name"], name));
  }
  return out;
}

export function fillFromGroup(draft: PlanDraft, c: number, group: SavedGroup, ctx: WhoContext): Change[] {
  return setTarget(draft, c, { kind: "people", ids: [...group.members], groupId: group.id }, ctx);
}

export const updateFromGroup = fillFromGroup;

export function setAthlete(draft: PlanDraft, c: number, id: string, on: boolean, ctx: WhoContext): Change[] {
  const column = draft.columns[c];
  if (!column) return [];
  const current = column.target?.kind === "people" ? column.target : { kind: "people" as const, ids: [] as string[] };
  const has = current.ids.includes(id);
  if (has === on) return [];
  const ids = on ? [...current.ids, id] : current.ids.filter((x) => x !== id);
  const next: ColumnTarget = current.groupId ? { kind: "people", ids, groupId: current.groupId } : { kind: "people", ids };
  return setTarget(draft, c, next, ctx);
}

/**
 * While no column names its athletes, each reaches whoever its name matches. Choosing who is in
 * one column pins every column to the athletes it reaches now, so the others keep theirs, then
 * gives column `c` its new target.
 */
export function pinFromNames(draft: PlanDraft, matched: (readonly string[])[], c: number, target: ColumnTarget | undefined, ctx: WhoContext): Change[] {
  const columns = draft.columns.map((col, i): PlanColumn => {
    if (i === c) {
      if (!target) return col;
      const name = nameFollowsTarget(col, ctx) ? (autoName(target, ctx) ?? col.name) : col.name;
      return { ...col, name, target };
    }
    if (isEveryone(col.name)) return { ...col, target: { kind: "everyone" } };
    const ids = matched[i] ?? [];
    return ids.length ? { ...col, target: { kind: "people", ids: [...ids] } } : col;
  });
  return [change(draft, ["columns"], columns)];
}

/** Who gets which column while columns are matched by name: the heads' athletes, by column. */
export function assignByNames(columns: PlanColumn[], matched: (readonly string[])[], athletes: TeamAthlete[]): Assignments {
  const byAthlete = new Map<string, string | null>();
  const byColumn = new Map<string, string[]>(columns.map((c, i) => [c.id, [...(matched[i] ?? [])]]));
  const nothing: string[] = [];
  for (const athlete of athletes) {
    const i = columns.findIndex((col, k) => !isEveryone(col.name) && matched[k]?.includes(athlete.id));
    const j = i >= 0 ? i : columns.findIndex((col) => isEveryone(col.name));
    byAthlete.set(athlete.id, j >= 0 ? columns[j]!.id : null);
    if (j < 0) nothing.push(athlete.id);
  }
  return { byAthlete, byColumn, nothing };
}

export type NewColumnKind = { kind: "group"; group: SavedGroup } | { kind: "athletes" } | { kind: "everyone" } | { kind: "blank" };

export function addTargetedColumn(draft: PlanDraft, how: NewColumnKind, ctx: WhoContext): { changes: Change[]; id: string } {
  const target: ColumnTarget | undefined =
    how.kind === "group"
      ? { kind: "people", ids: [...how.group.members], groupId: how.group.id }
      : how.kind === "athletes"
        ? { kind: "people", ids: [] }
        : how.kind === "everyone"
          ? { kind: "everyone" }
          : undefined;
  const name = autoName(target, ctx) ?? "New column";
  const { changes, id } = addColumn(draft, name);
  if (!target) return { changes, id };
  const columns = (changes[0]!.after as PlanColumn[]).map((col) => (col.id === id ? { ...col, target } : col));
  return { changes: [change(draft, ["columns"], columns)], id };
}
